import { useEffect, useState } from 'react';
import { v } from '../constants';
import { Wordmark } from './ui';
import { apiFetch } from '../lib/api';

// /verify-email?token=X — the email link lands here.
//
// The page auto-POSTs the token on mount so the user doesn't have to
// click anything. Three states:
//  1) verifying: in-flight
//  2) success: token accepted, account flagged emailVerified=true
//  3) failure: invalid/expired/used token
//
// We don't auto-redirect — the user reads the result + clicks through.

type State =
  | { kind: 'verifying' }
  | { kind: 'success' }
  | { kind: 'failure'; message: string };

const ERROR_COPY: Record<string, string> = {
  invalid_or_expired_token: "This verification link is expired or already used. Request a new one from your Account menu after signing in.",
  invalid_input: 'The verification link looks malformed. Try copying it again from your email.',
  rate_limited: 'Too many attempts. Try again in a few minutes.',
};

export function VerifyEmailPage() {
  const [state, setState] = useState<State>({ kind: 'verifying' });

  useEffect(() => {
    document.body.classList.add('hm-page-scroll');
    return () => { document.body.classList.remove('hm-page-scroll'); };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token') ?? '';
    if (!token) {
      setState({ kind: 'failure', message: 'No verification token in the URL. Make sure you opened the latest link we sent.' });
      return;
    }
    let cancelled = false;
    (async () => {
      const r = await apiFetch('/auth/verify-email', {
        method: 'POST',
        body: JSON.stringify({ token }),
      });
      if (cancelled) return;
      if (r.ok) {
        setState({ kind: 'success' });
      } else {
        const body = await r.json().catch(() => ({}));
        setState({
          kind: 'failure',
          message: ERROR_COPY[body.error] ?? body.error ?? "Couldn't verify your email. Try again in a moment.",
        });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div style={{
      minHeight: '100dvh',
      background: v('paper'),
      color: v('ink'),
    }}>
      <header style={{
        padding: '18px 22px',
        borderBottom: `1px solid ${v('ruleSoft')}`,
      }}>
        <a href="/" style={{ textDecoration: 'none', color: 'inherit', display: 'inline-block' }}>
          <Wordmark />
        </a>
      </header>
      <main style={{
        maxWidth: 420,
        margin: '0 auto',
        padding: '40px 22px 60px',
      }}>
        {state.kind === 'verifying' && (
          <>
            <h1 style={{ margin: '0 0 12px', fontSize: 26, fontWeight: 600 }}>Verifying…</h1>
            <p style={{ color: v('inkSoft') }}>Hold on.</p>
          </>
        )}
        {state.kind === 'success' && (
          <>
            <h1 style={{ margin: '0 0 12px', fontSize: 26, fontWeight: 600 }}>Email verified</h1>
            <p style={{ color: v('inkSoft'), lineHeight: 1.5 }}>
              Your email is confirmed. You're all set.
            </p>
            <a
              href="/"
              className="hm-primary"
              style={{ display: 'inline-block', marginTop: 16, textDecoration: 'none' }}
            >Back to Hexponents</a>
          </>
        )}
        {state.kind === 'failure' && (
          <>
            <h1 style={{ margin: '0 0 12px', fontSize: 26, fontWeight: 600 }}>Couldn't verify</h1>
            <p style={{ color: v('inkSoft'), lineHeight: 1.5 }}>{state.message}</p>
            <a
              href="/"
              className="hm-action"
              style={{ display: 'inline-block', marginTop: 16, textDecoration: 'none' }}
            >Back to Hexponents</a>
          </>
        )}
      </main>
    </div>
  );
}
